import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tag,
  Copy,
  Check,
  BarChart2,
  Calendar,
  ShieldOff,
  Clock,
  Loader2,
  Trash2,
  Pencil,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  deriveVoucherStatus,
  formatScopeLabel,
  formatVoucherValue,
  getApiErrorCode,
  scopeBadges,
  UpdateVoucherPayload,
  Voucher,
  VoucherScopeInput,
  VoucherType,
} from '@/client/types/voucher';
import {
  useVoucherDetailQuery,
  useVoucherRedemptionsQuery,
  useVoucherScopesQuery,
  useVoucherUsageQuery,
} from '@/client/api/voucher/voucher.query';
import {
  useDeleteVoucherMutation,
  useExpireVoucherMutation,
  useReplaceVoucherScopesMutation,
  useRevokeVoucherMutation,
  useUpdateVoucherMutation,
} from '@/client/api/voucher/voucher.mutation';
import VoucherScopesEditor from './VoucherScopesEditor';

interface VoucherDetailsDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  vid: number | null;
  types: VoucherType[];
  canEdit: boolean;
  canRevoke: boolean;
  canDelete: boolean;
  onClose: () => void;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-700',
  expired: 'bg-orange-100 text-orange-800',
  exhausted: 'bg-purple-100 text-purple-800',
  revoked: 'bg-red-100 text-red-800',
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="text-sm font-medium text-gray-900">{value}</div>
  </div>
);

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return raw;
  }
};

const toLocalInput = (raw?: string | null) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const VoucherDetailsDrawer: React.FC<VoucherDetailsDrawerProps> = ({
  isOpen,
  setIsOpen,
  vid,
  types,
  canEdit,
  canRevoke,
  canDelete,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState<'overview' | 'usage' | 'redemptions' | 'scopes' | 'edit'>('overview');
  const [redemptionsPage, setRedemptionsPage] = useState(1);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmExpire, setConfirmExpire] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [scopeDraft, setScopeDraft] = useState<VoucherScopeInput[]>([]);

  const { data: detail, isLoading: detailLoading } = useVoucherDetailQuery(isOpen ? vid : null);
  const voucher = detail ?? null;
  const typeMeta = useMemo(
    () => types.find((t) => t.vtid === voucher?.vtid),
    [types, voucher?.vtid],
  );

  const { data: usage, isLoading: usageLoading } = useVoucherUsageQuery(
    voucher?.vid ?? null,
    isOpen && tab === 'usage',
  );
  const { data: redemptionsData, isLoading: redemptionsLoading } = useVoucherRedemptionsQuery(
    voucher?.vid ?? null,
    redemptionsPage,
    25,
    isOpen && tab === 'redemptions',
  );
  const { data: scopesData, isLoading: scopesLoading } = useVoucherScopesQuery(
    voucher?.vid ?? null,
    isOpen && (tab === 'scopes' || tab === 'overview'),
  );

  const updateMutation = useUpdateVoucherMutation();
  const revokeMutation = useRevokeVoucherMutation();
  const expireMutation = useExpireVoucherMutation();
  const deleteMutation = useDeleteVoucherMutation();
  const scopesMutation = useReplaceVoucherScopesMutation();

  useEffect(() => {
    if (!isOpen) {
      setTab('overview');
      setConfirmRevoke(false);
      setConfirmExpire(false);
      setConfirmDelete(false);
      setRedemptionsPage(1);
      setScopeDraft([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!scopesData) return;
    setScopeDraft(
      (scopesData.scopes ?? []).map((s) => ({
        scope_type: s.scope_type,
        ref_id: s.ref_id,
        mode: s.mode,
      })),
    );
  }, [scopesData]);

  useEffect(() => {
    if (!voucher) return;
    setEditForm({
      name: voucher.name ?? '',
      description: voucher.description ?? '',
      value: String(voucher.value ?? ''),
      ccode: voucher.ccode ?? 'GHS',
      date_valid_from: toLocalInput(voucher.date_valid_from),
      date_valid_until: toLocalInput(voucher.date_valid_until),
      min_purchase_amount: voucher.min_purchase_amount != null ? String(voucher.min_purchase_amount) : '',
      max_discount_amount: voucher.max_discount_amount != null ? String(voucher.max_discount_amount) : '',
      max_redemptions: voucher.max_redemptions != null ? String(voucher.max_redemptions) : '',
      max_redemptions_per_user:
        voucher.max_redemptions_per_user != null ? String(voucher.max_redemptions_per_user) : '',
      uid_assigned: voucher.uid_assigned != null ? String(voucher.uid_assigned) : '',
      active: String(voucher.active ?? 1),
    });
  }, [voucher]);

  if (!isOpen) return null;

  const status = voucher ? deriveVoucherStatus(voucher) : 'inactive';
  const hasScopes =
    (scopesData?.scopes?.length ?? 0) > 0 ||
    Boolean(scopesData?.legacy_toid_rule) ||
    (voucher?.toid != null);
  const badges = voucher ? scopeBadges(voucher, hasScopes && !scopesData?.unrestricted) : [];
  const valueType = (typeMeta?.value_type || 'FIXED').toUpperCase();
  const busy =
    updateMutation.isPending ||
    revokeMutation.isPending ||
    expireMutation.isPending ||
    deleteMutation.isPending ||
    scopesMutation.isPending;

  const handleCopy = () => {
    if (!voucher) return;
    navigator.clipboard.writeText(voucher.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExpire = async () => {
    if (!voucher) return;
    if (!confirmExpire) {
      setConfirmExpire(true);
      return;
    }
    try {
      await expireMutation.mutateAsync(voucher.vid);
      toast.success(`Voucher "${voucher.code}" deactivated`, { richColors: true });
      setConfirmExpire(false);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to expire voucher', { richColors: true });
      setConfirmExpire(false);
    }
  };

  const handleReactivate = async () => {
    if (!voucher) return;
    try {
      await updateMutation.mutateAsync({ vid: voucher.vid, payload: { active: 1 } });
      toast.success(`Voucher "${voucher.code}" reactivated`, { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to reactivate voucher', { richColors: true });
    }
  };

  const handleRevoke = async () => {
    if (!voucher) return;
    if (!confirmRevoke) {
      setConfirmRevoke(true);
      return;
    }
    try {
      await revokeMutation.mutateAsync(voucher.vid);
      toast.success(`Voucher "${voucher.code}" revoked`, { richColors: true });
      setConfirmRevoke(false);
    } catch (error: any) {
      const code = getApiErrorCode(error);
      if (code === 'already_redeemed') {
        toast.error('Cannot revoke a redeemed voucher', { richColors: true });
      } else if (code === 'already_revoked') {
        toast.error('Voucher is already revoked', { richColors: true });
      } else {
        toast.error(error?.message || 'Failed to revoke voucher', { richColors: true });
      }
      setConfirmRevoke(false);
    }
  };

  const handleDelete = async () => {
    if (!voucher) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deleteMutation.mutateAsync(voucher.vid);
      toast.success(`Voucher "${voucher.code}" deleted`, { richColors: true });
      onClose();
    } catch (error: any) {
      const code = getApiErrorCode(error);
      if (code === 'voucher_redeemed') {
        toast.error('This voucher was redeemed — revoke it instead of deleting', { richColors: true });
      } else {
        toast.error(error?.message || 'Failed to delete voucher', { richColors: true });
      }
      setConfirmDelete(false);
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!voucher) return;

    const payload: UpdateVoucherPayload = {
      name: editForm.name.trim() || null,
      description: editForm.description.trim() || null,
      ccode: editForm.ccode.trim() || 'GHS',
      active: Number(editForm.active) === 1 ? 1 : 0,
    };

    // ACCESS passes carry no value; leave it untouched.
    if (valueType !== 'NONE') {
      const value = Number(editForm.value);
      if (!Number.isFinite(value) || value <= 0) {
        toast.error('Enter a positive value', { richColors: true });
        return;
      }
      if (valueType === 'PERCENT' && value > 100) {
        toast.error('Percent must be 0–100', { richColors: true });
        return;
      }
      payload.value = value;
    }

    // date_valid_from is NOT NULL in ffVoucher; a cleared field keeps the
    // current start rather than sending null.
    if (editForm.date_valid_from) {
      const d = new Date(editForm.date_valid_from);
      if (!Number.isNaN(d.getTime())) payload.date_valid_from = d.toISOString();
    }
    if (editForm.date_valid_until) {
      const d = new Date(editForm.date_valid_until);
      if (!Number.isNaN(d.getTime())) payload.date_valid_until = d.toISOString();
    } else {
      payload.date_valid_until = null;
    }
    if (
      payload.date_valid_from &&
      payload.date_valid_until &&
      new Date(payload.date_valid_until) <= new Date(payload.date_valid_from)
    ) {
      toast.error('End date must be after start date', { richColors: true });
      return;
    }

    payload.min_purchase_amount =
      editForm.min_purchase_amount.trim() === '' ? null : Number(editForm.min_purchase_amount);
    if (valueType === 'PERCENT') {
      payload.max_discount_amount =
        editForm.max_discount_amount.trim() === '' ? null : Number(editForm.max_discount_amount);
    }
    payload.max_redemptions =
      editForm.max_redemptions.trim() === '' ? null : Number(editForm.max_redemptions);
    payload.max_redemptions_per_user =
      editForm.max_redemptions_per_user.trim() === ''
        ? null
        : Number(editForm.max_redemptions_per_user);
    payload.uid_assigned =
      editForm.uid_assigned.trim() === '' ? null : Number(editForm.uid_assigned);

    try {
      await updateMutation.mutateAsync({ vid: voucher.vid, payload });
      toast.success('Voucher updated', { richColors: true });
      setTab('overview');
    } catch (error: any) {
      const code = getApiErrorCode(error);
      if (code === 'invalid_percentage') {
        toast.error('Percent must be 0–100', { richColors: true });
      } else if (code === 'no_valid_fields') {
        toast.error('No valid fields to update', { richColors: true });
      } else {
        toast.error(error?.message || 'Failed to update voucher', { richColors: true });
      }
    }
  };

  const handleSaveScopes = async () => {
    if (!voucher) return;
    try {
      await scopesMutation.mutateAsync({
        vid: voucher.vid,
        payload: { scopes: scopeDraft },
      });
      toast.success(
        scopeDraft.length === 0
          ? 'Scopes cleared — voucher is unrestricted'
          : 'Scopes updated',
        { richColors: true },
      );
    } catch (error: any) {
      const code = getApiErrorCode(error);
      if (code === 'invalid_scope') {
        toast.error('Invalid scope — check type, ref id, and mode', { richColors: true });
      } else {
        toast.error(error?.message || 'Failed to update scopes', { richColors: true });
      }
    }
  };

  const tabs: { id: typeof tab; label: string; show?: boolean }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'usage', label: 'Usage' },
    { id: 'redemptions', label: 'Redemptions' },
    { id: 'scopes', label: 'Scopes' },
    { id: 'edit', label: 'Edit', show: canEdit },
  ];

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-blue-600" />
            {detailLoading ? 'Loading…' : voucher?.code ?? 'Voucher'}
          </DrawerTitle>
          <DrawerDescription>
            {voucher?.name || typeMeta?.name || 'Voucher details'}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex gap-1 overflow-x-auto border-b border-gray-100 px-4 pt-2">
          {tabs
            .filter((t) => t.show !== false)
            .map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`whitespace-nowrap rounded-t-md px-3 py-2 text-sm font-medium ${
                  tab === t.id
                    ? 'border-b-2 border-blue-600 text-blue-700'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {detailLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {!detailLoading && voucher && tab === 'overview' && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[status]}`}
                >
                  {status.toUpperCase()}
                </span>
                {badges.map((b) => (
                  <span
                    key={b}
                    className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                  >
                    {b}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-3">
                <code className="flex-1 font-mono text-lg font-bold tracking-wider text-gray-900">
                  {voucher.code}
                </code>
                <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="Public ID (ffid)" value={voucher.ffid || '—'} />
                <InfoRow label="VID" value={voucher.vid} />
                <InfoRow label="Type" value={typeMeta?.name || `vtid ${voucher.vtid}`} />
                <InfoRow
                  label="Value"
                  value={formatVoucherValue(voucher.value, valueType, voucher.ccode)}
                />
                <InfoRow label="Currency" value={voucher.ccode} />
                <InfoRow
                  label="Redemptions"
                  value={`${voucher.redemption_count ?? 0}${
                    voucher.max_redemptions != null ? ` / ${voucher.max_redemptions}` : ' (single-use default)'
                  }`}
                />
                <InfoRow label="Valid from" value={formatDate(voucher.date_valid_from)} />
                <InfoRow label="Valid until" value={formatDate(voucher.date_valid_until)} />
                <InfoRow label="Min purchase" value={voucher.min_purchase_amount ?? '—'} />
                <InfoRow label="Max discount" value={voucher.max_discount_amount ?? '—'} />
                <InfoRow label="Org issuer" value={voucher.oid_issuer ?? '—'} />
                <InfoRow label="Store" value={voucher.storeid ?? '—'} />
                <InfoRow label="Assigned user" value={voucher.uid_assigned ?? '—'} />
                <InfoRow label="Legacy toid" value={voucher.toid ?? '—'} />
              </div>

              {(scopesLoading || scopesData) && (
                <div>
                  <p className="mb-1.5 text-xs text-gray-500">Targeting scopes</p>
                  {scopesLoading && <Skeleton className="h-10 w-full" />}
                  {!scopesLoading && scopesData?.unrestricted && (
                    <p className="text-sm text-gray-600">Unrestricted — applies to everything</p>
                  )}
                  {!scopesLoading && !scopesData?.unrestricted && (
                    <ul className="space-y-1">
                      {(scopesData?.scopes ?? []).map((s, i) => (
                        <li
                          key={s.vsid ?? `${s.scope_type}-${s.ref_id}-${i}`}
                          className="rounded-md bg-gray-50 px-2.5 py-1.5 text-sm text-gray-800"
                        >
                          {formatScopeLabel(s)}
                        </li>
                      ))}
                      {scopesData?.legacy_toid_rule && (
                        <li className="rounded-md bg-amber-50 px-2.5 py-1.5 text-sm text-amber-900">
                          Legacy: {formatScopeLabel(scopesData.legacy_toid_rule)}
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              )}

              {voucher.description && (
                <InfoRow label="Description" value={voucher.description} />
              )}
            </div>
          )}

          {!detailLoading && voucher && tab === 'usage' && (
            <div className="space-y-4">
              {usageLoading && <Skeleton className="h-28 w-full" />}
              {!usageLoading && usage && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow label="Redeemed" value={usage.redemption_count} />
                    <InfoRow
                      label="Max"
                      value={usage.max_redemptions ?? 'Unlimited / single-use rules'}
                    />
                    <InfoRow
                      label="Remaining"
                      value={usage.remaining ?? '—'}
                    />
                    <InfoRow
                      label="Exhausted"
                      value={usage.is_exhausted ? 'Yes' : 'No'}
                    />
                  </div>
                  {usage.max_redemptions != null && usage.max_redemptions > 0 && (
                    <div>
                      <div className="mb-1 flex justify-between text-xs text-gray-500">
                        <span>Usage</span>
                        <span>
                          {usage.redemption_count} / {usage.max_redemptions}
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-100">
                        <div
                          className="h-2 rounded-full bg-blue-500"
                          style={{
                            width: `${Math.min(
                              (usage.redemption_count / usage.max_redemptions) * 100,
                              100,
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!detailLoading && voucher && tab === 'redemptions' && (
            <div className="space-y-3">
              {redemptionsLoading && (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              )}
              {!redemptionsLoading && (redemptionsData?.redemptions?.length ?? 0) === 0 && (
                <p className="py-8 text-center text-sm text-gray-500">No redemptions yet</p>
              )}
              {!redemptionsLoading &&
                redemptionsData?.redemptions?.map((r) => (
                  <div
                    key={r.vrid}
                    className="rounded-lg border border-gray-100 bg-white p-3 text-sm"
                  >
                    <div className="flex justify-between gap-2">
                      <span className="font-medium text-gray-900">User {r.uid}</span>
                      <span className="text-xs text-gray-500">{formatDate(r.date_redeemed)}</span>
                    </div>
                    <p className="mt-1 text-gray-600">
                      Applied {voucher.ccode} {r.amount_applied}
                      {r.original_amount != null ? ` of ${voucher.ccode} ${r.original_amount}` : ''}
                      {r.invid != null ? ` · invoice ${r.invid}` : ''}
                    </p>
                  </div>
                ))}
              {(redemptionsData?.pagination?.has_more || redemptionsPage > 1) && (
                <div className="flex justify-between pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={redemptionsPage <= 1}
                    onClick={() => setRedemptionsPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!redemptionsData?.pagination?.has_more}
                    onClick={() => setRedemptionsPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}

          {!detailLoading && voucher && tab === 'scopes' && (
            <div className="space-y-4">
              {scopesLoading && <Skeleton className="h-40 w-full" />}
              {!scopesLoading && (
                <>
                  <VoucherScopesEditor
                    value={scopeDraft}
                    onChange={setScopeDraft}
                    disabled={!canEdit || busy}
                    hint="PUT replaces the full set. Send an empty list to clear (unrestricted)."
                  />
                  {canEdit && (
                    <Button
                      type="button"
                      className="w-full"
                      disabled={busy}
                      onClick={handleSaveScopes}
                    >
                      {scopesMutation.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Save scopes
                    </Button>
                  )}
                  {!canEdit && (
                    <p className="text-xs text-gray-500">
                      You need <code className="rounded bg-gray-100 px-1">voucher.edit</code> to
                      change scopes.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {!detailLoading && voucher && tab === 'edit' && canEdit && (
            <form onSubmit={handleSaveEdit} className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Description</Label>
                  <Input
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                {valueType !== 'NONE' && (
                  <div className="space-y-1.5">
                    <Label>{valueType === 'PERCENT' ? 'Percent off' : 'Value'}</Label>
                    <Input
                      type="number"
                      step="any"
                      min={0}
                      max={valueType === 'PERCENT' ? 100 : undefined}
                      value={editForm.value}
                      onChange={(e) => setEditForm((f) => ({ ...f, value: e.target.value }))}
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <Input
                    value={editForm.ccode}
                    onChange={(e) => setEditForm((f) => ({ ...f, ccode: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Valid from</Label>
                  <Input
                    type="datetime-local"
                    value={editForm.date_valid_from}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, date_valid_from: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Valid until</Label>
                  <Input
                    type="datetime-local"
                    value={editForm.date_valid_until}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, date_valid_until: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Min purchase</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editForm.min_purchase_amount}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, min_purchase_amount: e.target.value }))
                    }
                  />
                </div>
                {valueType === 'PERCENT' && (
                  <div className="space-y-1.5">
                    <Label>Max discount</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editForm.max_discount_amount}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, max_discount_amount: e.target.value }))
                      }
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Max redemptions</Label>
                  <Input
                    type="number"
                    value={editForm.max_redemptions}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, max_redemptions: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Max per user</Label>
                  <Input
                    type="number"
                    value={editForm.max_redemptions_per_user}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, max_redemptions_per_user: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Assigned user ID</Label>
                  <Input
                    type="number"
                    value={editForm.uid_assigned}
                    onChange={(e) => setEditForm((f) => ({ ...f, uid_assigned: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Active</Label>
                  <select
                    value={editForm.active}
                    onChange={(e) => setEditForm((f) => ({ ...f, active: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full">
                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save changes
              </Button>
            </form>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {canRevoke && voucher && status !== 'revoked' && voucher.active === 1 && (
              <Button
                type="button"
                variant={confirmExpire ? 'destructive' : 'outline'}
                size="sm"
                disabled={busy}
                onClick={handleExpire}
              >
                <Clock className="mr-1.5 h-4 w-4" />
                {confirmExpire ? 'Confirm expire' : 'Expire'}
              </Button>
            )}
            {canEdit && voucher && voucher.active === 0 && status !== 'revoked' && (
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={handleReactivate}>
                <BarChart2 className="mr-1.5 h-4 w-4" />
                Reactivate
              </Button>
            )}
            {canRevoke && voucher && status !== 'revoked' && (
              <Button
                type="button"
                variant={confirmRevoke ? 'destructive' : 'outline'}
                size="sm"
                disabled={busy}
                onClick={handleRevoke}
              >
                <Ban className="mr-1.5 h-4 w-4" />
                {confirmRevoke ? 'Confirm revoke' : 'Revoke'}
              </Button>
            )}
            {canDelete && voucher && (voucher.redemption_count ?? 0) === 0 && voucher.redeemed !== 1 && (
              <Button
                type="button"
                variant={confirmDelete ? 'destructive' : 'outline'}
                size="sm"
                disabled={busy}
                onClick={handleDelete}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                {confirmDelete ? 'Confirm delete' : 'Delete'}
              </Button>
            )}
            {canEdit && tab !== 'edit' && (
              <Button type="button" variant="outline" size="sm" onClick={() => setTab('edit')}>
                <Pencil className="mr-1.5 h-4 w-4" />
                Edit
              </Button>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
          {confirmRevoke && (
            <p className="text-xs text-red-600">
              <ShieldOff className="mr-1 inline h-3 w-3" />
              Revoke permanently kills the voucher but keeps the audit row.
            </p>
          )}
          {confirmExpire && (
            <p className="text-xs text-orange-600">
              <Calendar className="mr-1 inline h-3 w-3" />
              Expire soft-deactivates the voucher (reversible via Reactivate).
            </p>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default VoucherDetailsDrawer;
